import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Building2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

export default function Orgs() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tagline: "", mission: "", industry: "", stage: "", location: "", website: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/orgs?mine=true");
      setOrgs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orgs", form);
      toast.success("Organization created");
      setOpen(false);
      setForm({ name: "", tagline: "", mission: "", industry: "", stage: "", location: "", website: "" });
      nav(`/app/orgs/${data.id}`);
    } catch (e) {
      toast.error("Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="orgs-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps mb-1">Your organizations</div>
          <h1 className="text-3xl tracking-tight font-semibold">Organizations</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="new-org-btn"><Plus className="h-4 w-4 mr-1" />New organization</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create organization</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="org-name" className="mt-1.5" /></div>
              <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({...form, tagline: e.target.value})} data-testid="org-tagline" className="mt-1.5" /></div>
              <div><Label>Mission</Label><Textarea rows={2} value={form.mission} onChange={(e) => setForm({...form, mission: e.target.value})} data-testid="org-mission" className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} data-testid="org-industry" className="mt-1.5" /></div>
                <div><Label>Stage</Label><Input value={form.stage} onChange={(e) => setForm({...form, stage: e.target.value})} placeholder="Seed, Series A…" data-testid="org-stage" className="mt-1.5" /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} data-testid="org-location" className="mt-1.5" /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} data-testid="org-website" className="mt-1.5" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={busy} data-testid="org-create-submit">{busy ? "Creating…" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : orgs.length === 0 ? (
        <Card className="p-10 text-center">
          <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <div className="font-medium">No organizations yet</div>
          <div className="text-sm text-muted-foreground mt-1">Create one to start posting roles.</div>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {orgs.map((o) => (
            <Link key={o.id} to={`/app/orgs/${o.id}`} data-testid={`org-${o.id}`}>
              <Card className="p-5 h-full hover:border-primary/50 transition-colors duration-200">
                <div className="label-caps mb-1">{o.industry || "Company"}</div>
                <div className="text-lg font-medium tracking-tight">{o.name}</div>
                {o.tagline && <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{o.tagline}</div>}
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {o.stage && <Badge variant="secondary" className="text-[10px] font-mono">{o.stage}</Badge>}
                  {o.location && <Badge variant="outline" className="text-[10px]">{o.location}</Badge>}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
